import { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Collapse, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import ChevronDown from '@eduzz/houston-icons/ChevronDown';
import ChevronUp from '@eduzz/houston-icons/ChevronUp';
import { CourseTheme } from 'assets/themes/Course';
import { useTrottle } from 'hooks/trottle';

interface IProps {
  text: string;
  files: any;
  iframeUrl: any;
  iframeConfig: any;
  type: 'Descricao' | 'Iframe';
}

const useStyles = makeStyles<CourseTheme>((theme) => ({
  description: {
    color: theme.variables.courseTheme.text,
    overflow: 'hidden',
    boxOrient: 'vertical',
    lineHeight: '23px',
    overflowWrap: 'break-word',
  },
  descriptionContainer: {
    padding: '20px 0',
    [theme.breakpoints.down('md')]: {
      padding: 20,
    },
  },
  button: {
    cursor: 'pointer',
    '& svg': {
      color: theme.variables.courseTheme.text,
    },
    '& p': {
      color: theme.variables.courseTheme.text,
      display: 'block',
      marginRight: theme.spacing(3),
      fontWeight: 600,
      textTransform: 'lowercase',
      '&:first-letter': {
        textTransform: 'uppercase',
      },
    },
  },
}));

const Description = ({ text, iframeUrl, iframeConfig, type }: IProps) => {
  const [totalNumberOfLines, setTotalNumberOfLines] = useState(0);
  const [descriptionExpanded, setDescriptionExpanded] = useState<boolean>(true);
  const descriptionRef = useRef<HTMLParagraphElement>();
  const classes = useStyles();
  const trottle = useTrottle();

  const isNumberOfLinesEnough = useCallback(() => {
    return totalNumberOfLines > 12;
  }, [totalNumberOfLines]);

  useEffect(() => {
    const ref = descriptionRef.current;
    if (!ref) return null;
    const images = Array.from(ref.querySelectorAll('img'));
    if (images.length === 0) setTotalNumberOfLines(Math.floor(descriptionRef.current?.offsetHeight / 10));

    const onLoad = () => {
      trottle(() => {
        setTotalNumberOfLines(Math.floor(descriptionRef.current?.offsetHeight / 10));
      }, 350);
    };
    if (images.length > 0) {
      images.forEach((image) => image.addEventListener('load', onLoad));
    }
    setDescriptionExpanded(false);
    return () => {
      if (images.length > 0) {
        images.forEach((image) => image.removeEventListener('load', onLoad));
      }
    };
  }, [descriptionRef, trottle]);

  if (!text && !iframeUrl) return null;

  return (
    <div aria-label='Descrição da aula' id='lesson-description-container' className={classes.descriptionContainer}>
      {type === 'Iframe' && (
        <iframe
          title='lesson_description'
          src={iframeUrl}
          height={iframeConfig}
          allow='geolocation; microphone; camera'
        />
      )}
      {type !== 'Iframe' && (
        <Collapse in={descriptionExpanded} collapsedSize={110}>
          <Typography ref={descriptionRef} className={classes.description} dangerouslySetInnerHTML={{ __html: text }} />
        </Collapse>
      )}
      {isNumberOfLinesEnough() && (
        <Button
          fullWidth
          aria-label='Visualizar descrição completa da aula ou diminuir'
          variant='text'
          className={classes.button}
          onClick={() => setDescriptionExpanded((prev) => !prev)}
        >
          <Typography variant='body2' component='p'>
            {descriptionExpanded ? 'Ver Menos' : 'Ver Mais'}
          </Typography>
          {descriptionExpanded ? <ChevronUp /> : <ChevronDown />}
        </Button>
      )}
    </div>
  );
};

export default Description;
